import { useContext, useState } from 'react';
import { Ribbon, RibbonProps } from '@bitrise/bitkit';
import { format, parseISO } from 'date-fns';
import { planSelectorPagePath } from '@/utils/monolithApiRouteService';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { useQueryParam } from '@/hooks/useQueryParam';
import { mergeQueryParam } from '@/utils/queryParams';
import { useMutateUserMetadata } from '@/hooks/api/useUserMetadata';
import ExtendYourLimitDialog from '../ExtendYourLimitDialog/ExtendYourLimitDialog';

const TimeBasedTrialBanner = () => {
  const { mutate } = useMutateUserMetadata({ transform: false });
  const { account } = useContext(GlobalPropsContext);
  const [isBannerVisible, setBannerVisible] = useState(account?.onTimeBasedTrial);
  const isExtendDialogOpen = useQueryParam('show-extend-limit') === 'true';
  if (!account) {
    return null;
  }
  const { slug, timeBasedTrial } = account;

  const onRibbonClose = (key: string) => {
    mutate({ [key]: true });
    setBannerVisible(false);
  };

  if (!isBannerVisible) {
    return null;
  }

  const openExtendDialog = () => {
    mergeQueryParam('show-extend-limit', 'true');
  };

  const closeExtendDialog = () => {
    mergeQueryParam('show-extend-limit', null);
  };

  const getRibbonProps = (): RibbonProps | null => {
    if (timeBasedTrial?.showOverFairUsageLimitBanner) {
      return {
        action: {
          label: 'Extend limit',
          onClick: openExtendDialog,
        },
        children: <strong>Your workspace usage during trial has exceeded our fair usage limit.</strong>,
        colorScheme: 'red',
      };
    }

    if (timeBasedTrial?.showTrialEndedBanner) {
      return {
        children: <strong>Your 30 day free trial has ended</strong>,
        colorScheme: 'red',
      };
    }

    if (timeBasedTrial?.showXDaysUntilTrialEndBanner) {
      return {
        action: {
          href: planSelectorPagePath(slug),
          label: 'Select plan',
        },
        children: (
          <p>
            <strong>You have {timeBasedTrial?.xDaysUntilTrialEnd} days left</strong> on the free Bitrise trial.
          </p>
        ),
        colorScheme: 'yellow',
      };
    }

    if (timeBasedTrial?.showUnderTrialBanner) {
      return {
        action: {
          href: planSelectorPagePath(slug),
          label: 'View plans',
        },
        children: (
          <p>
            <strong>Free trial.</strong> You can use Bitrise for free until{' '}
            {format(parseISO(timeBasedTrial?.endTime || ''), 'MMMM d, yyyy')}.
          </p>
        ),
        colorScheme: 'blue',
        onClose: () => onRibbonClose(`time_based_trial_under_trial_banner_closed_on_${slug}`),
      };
    }

    return null;
  };

  const ribbonProps = getRibbonProps();

  return (
    <>
      {ribbonProps && <Ribbon {...ribbonProps} />}
      <ExtendYourLimitDialog isOpen={isExtendDialogOpen} onClose={closeExtendDialog} />
    </>
  );
};

export default TimeBasedTrialBanner;
